import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Lock, Award } from 'lucide-react';
import { HERO_IMAGE } from '../data/products';
import { useAccess } from '../context/AccessContext';

const EntryGate = () => {
  const navigate = useNavigate();
  const { hasAccess, setAgeVerified } = useAccess();
  const [showAgeGate, setShowAgeGate] = useState(true);

  const handleAgeConfirm = () => {
    setShowAgeGate(false);
    // Store age verification
    if (setAgeVerified) {
      setAgeVerified(true);
    }
    localStorage.setItem('secret13_age_verified', 'true');
  };

  const handleExit = () => {
    window.location.href = 'https://google.com';
  };

  const handleEnter = () => {
    if (hasAccess) {
      navigate('/collection');
    } else {
      navigate('/access');
    }
  };

  const handleExplore = () => {
    if (hasAccess) {
      navigate('/collection');
    } else {
      navigate('/access');
    }
  };

  // Check if age was already verified this session
  React.useEffect(() => {
    const ageVerified = localStorage.getItem('secret13_age_verified');
    if (ageVerified === 'true') {
      setShowAgeGate(false);
    }
  }, []);

  // Text animation variants
  const textVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: (delay) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.2,
        delay: delay,
        ease: [0.25, 0.1, 0.25, 1]
      }
    })
  };

  return (
    <div className="fixed inset-0 bg-[#050505] overflow-hidden">
      {/* Background Image with Slow Zoom */}
      <motion.div
        initial={{ scale: 1, opacity: 0 }}
        animate={{ scale: 1.1, opacity: 1 }}
        transition={{ 
          scale: { duration: 30, ease: "linear" },
          opacity: { duration: 2, ease: "easeOut" }
        }}
        className="absolute inset-0 overflow-hidden flex items-center"
      >
        <img
          src={HERO_IMAGE}
          alt="Secret 13"
          className="w-full opacity-35"
        />
      </motion.div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />

      {/* Age Gate Overlay */}
      <AnimatePresence>
        {showAgeGate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 z-50 bg-[#050505]/95 backdrop-blur-sm flex items-center justify-center px-6"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center max-w-md"
            >
              {/* Brand */}
              <h1 className="font-serif text-4xl sm:text-5xl font-light text-zinc-50 mb-6">
                Secret 13
              </h1>

              {/* Brand Line */}
              <p className="font-sans text-sm text-zinc-500 mb-10">
                A collection not meant for everyone.
              </p>

              {/* Age Question */}
              <p className="font-serif text-2xl sm:text-3xl font-light text-zinc-200 mb-10">
                Are you 21 or older?
              </p>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
                <button
                  data-testid="age-yes-button"
                  onClick={handleAgeConfirm}
                  className="bg-white text-black px-12 py-4 font-sans text-xs font-medium tracking-[0.2em] uppercase hover:bg-zinc-200 transition-all duration-300 min-w-[160px]"
                >
                  Yes, Enter
                </button>
                <button
                  data-testid="age-exit-button"
                  onClick={handleExit}
                  className="border border-zinc-700 text-zinc-400 px-12 py-4 font-sans text-xs font-medium tracking-[0.2em] uppercase hover:border-zinc-500 hover:text-zinc-300 transition-all duration-300 min-w-[160px]"
                >
                  Exit
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content (visible after age gate) */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6">
        {/* Logo / Brand */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: showAgeGate ? 0 : 1, y: showAgeGate ? 20 : 0 }}
          transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
          className="text-center mb-12"
        >
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-zinc-50 mb-2">
            Secret 13
          </h1>
          <div className="w-12 h-px bg-white/20 mx-auto mt-4" />
        </motion.div>

        {/* Main Text */}
        <div className="text-center mb-12 max-w-lg">
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: showAgeGate ? 0 : 1, y: showAgeGate ? 15 : 0 }}
            transition={{ duration: 1.2, delay: 0.5 }}
            className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-zinc-50 mb-6 tracking-wide"
          >
            Private. Curated. Limited.
          </motion.p>
          
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: showAgeGate ? 0 : 1, y: showAgeGate ? 15 : 0 }}
            transition={{ duration: 1.2, delay: 0.7 }}
            className="font-sans text-sm sm:text-base text-zinc-500 tracking-wide"
          >
            A refined members-only collection.
          </motion.p>
        </div>

        {/* Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: showAgeGate ? 0 : 1, y: showAgeGate ? 20 : 0 }}
          transition={{ duration: 1, delay: 0.9, ease: "easeOut" }}
          className="flex flex-col sm:flex-row items-center gap-4"
        >
          <button
            data-testid="enter-button"
            onClick={handleEnter}
            className="bg-white text-black px-12 py-4 font-sans text-xs font-medium tracking-[0.2em] uppercase hover:bg-zinc-200 transition-all duration-300 hover:-translate-y-0.5 min-w-[200px]"
          >
            Enter
          </button>
          <button
            data-testid="explore-button"
            onClick={handleExplore}
            className="border border-zinc-700 text-zinc-400 px-10 py-4 font-sans text-xs font-medium tracking-[0.2em] uppercase hover:border-white hover:text-white transition-all duration-500 min-w-[200px]"
          >
            Explore Collection
          </button>
        </motion.div>

        {/* Subtle Members Only Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: showAgeGate ? 0 : 1 }}
          transition={{ duration: 1.5, delay: 1.2 }}
          className="mt-16"
        >
          <div className="flex items-center gap-2">
            <div className="w-1 h-1 bg-zinc-600 rounded-full" />
            <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-zinc-600">
              Members Only
            </span>
            <div className="w-1 h-1 bg-zinc-600 rounded-full" />
          </div>
        </motion.div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: showAgeGate ? 0 : 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="absolute bottom-12 left-0 right-0"
        >
          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12 px-6">
            <div className="flex items-center gap-2 text-zinc-600">
              <Shield className="w-4 h-4" />
              <span className="font-sans text-[10px] tracking-widest uppercase">21+ Verified</span>
            </div>
            <div className="flex items-center gap-2 text-zinc-600">
              <Lock className="w-4 h-4" />
              <span className="font-sans text-[10px] tracking-widest uppercase">Discreet & Secure</span>
            </div>
            <div className="flex items-center gap-2 text-zinc-600">
              <Award className="w-4 h-4" />
              <span className="font-sans text-[10px] tracking-widest uppercase">Premium Quality</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default EntryGate;
